"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
export default function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="19601" id="main-navigation" className="sticky top-0 z-50 w-full border-b border-border transition-colors duration-300 bg-primary-foreground ![padding:4px_0px_4px_0px] ![margin:0px_0px_4px_0px]">
      <div className="w-full ![padding:20px_48px_20px_48px]">
        <div className="grid grid-cols-12 items-center gap-6 !bg-transparent">
          <div className="col-span-6 md:col-span-3 flex items-center justify-start">
            <Link to="/" aria-label="InfraPM — Home" className="inline-flex items-center">
              <img src="https://infrapm.com/wp-content/uploads/2026/06/3-1.png" alt="InfraPM" className="!w-[96px] !h-[100px]" loading="lazy" />
            </Link>
          </div>

          <nav className="hidden md:col-span-9 md:flex items-center justify-end gap-7">
            <Link to="/about/" className="font-medium text-foreground transition-colors duration-200 !text-lg hover:text-primary">
              About InfraPM
            </Link>

            <Link to="/services/" className="font-medium text-foreground transition-colors duration-200 !text-lg hover:text-primary">
              Services
            </Link>

            <Button asChild className="px-4 py-2 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs has-[>svg]:px-3 h-auto bg-primary font-medium transition-all duration-[220ms] ease-out font-default !text-[rgba(0,0,0,1)] !p-0 !text-lg disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90">
              <Link to="/contact/" className="text-black">
                Contact
              </Link>
            </Button>
          </nav>

          <div className="col-span-6 md:hidden flex items-center justify-end">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" className="h-auto bg-transparent px-2 py-1 text-base font-medium text-foreground hover:bg-transparent hover:text-primary font-default" aria-label="Open menu">
                  <Menu className="size-4" />
                  <span>Menu</span>
                </Button>
              </SheetTrigger>

              <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-0 flex flex-col">
                <SheetTitle className="sr-only">Navigation</SheetTitle>

                <div className="flex items-center justify-between px-6 py-5 border-b border-border">
                  <Link to="/" onClick={() => setMobileOpen(false)} aria-label="InfraPM — Home" className="inline-flex items-center">
                    <img src="https://infrapm.com/wp-content/uploads/2026/06/3-1.png" alt="InfraPM" className="w-auto !w-[75px] !h-[79px]" loading="lazy" />
                  </Link>
                </div>

                <nav className="flex flex-1 flex-col overflow-y-auto px-6 py-8">
                  <Link to="/about/" onClick={() => setMobileOpen(false)} className="border-b border-border py-4 text-lg font-medium text-foreground hover:text-primary">
                    About InfraPM
                  </Link>

                  <Link to="/services/" onClick={() => setMobileOpen(false)} className="border-b border-border py-4 text-lg font-medium text-foreground hover:text-primary">
                    Services
                  </Link>

                  <Link to="/contact/" onClick={() => setMobileOpen(false)} className="border-b border-border py-4 text-lg font-medium text-foreground hover:text-primary">
                    Contact
                  </Link>
                </nav>

                <div className="px-6 py-6 border-t border-border">
                  <Button asChild className="w-full h-auto bg-primary px-8 py-4 text-sm font-medium text-primary-foreground transition-all duration-[220ms] ease-out hover:bg-primary/90 font-default">
                    <Link to="/contact/" onClick={() => setMobileOpen(false)} className="text-black">
                      Contact
                    </Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}